import React, { useCallback, useEffect, useRef, useState, memo } from 'react';
import styled from 'styled-components';
import { SpeakerSelectionMenuProps, GroupedSpeakerData } from './interfaces';
import { defaultTheme } from './theme';
import InsertTextModal from './modals/InsertTextModal';
import ClipSaveConfirmationModal from './modals/ClipSaveConfirmationModal';
import { SELECTION_COLORS } from '../constants/selectionColors';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
/**
 * Menu for assigning speakers to selected text
 * Appears when text is selected in the transcript
 */
const SpeakerSelectionMenu: React.FC<SpeakerSelectionMenuProps> = memo(({
  position,
  isOpen,
  onClose,
  editorStore,
  onCreateNewSpeaker,
  onFineAlign,
  onForceAlign,
  menuTitle,
  onRequestGlobalReplace
}) => {
  // Reference to the menu container
  const modalRef = useRef<HTMLDivElement>(null);
  
  // State for speaker data and modals
  const [speakerData, setSpeakerData] = useState<GroupedSpeakerData>({
    mapped: [],
    unmapped: [],
    story: [],
    album: []
  });
  
  // Insert Text Modal state
  const [showInsertTextModal, setShowInsertTextModal] = useState(false);
  const [showColorSubmenu, setShowColorSubmenu] = useState(false);
  const [colorSubmenuRef, setColorSubmenuRef] = useState<HTMLElement | null>(null);
  const [finalPosition, setFinalPosition] = useState({
    top: position.y,
    left: position.x
  });
  
  // Clip save modal state
  const [clipSaveState, setClipSaveState] = useState<{
    isOpen: boolean;
    isLoading: boolean;
    videoId?: string;
    error?: string;
  }>({
    isOpen: false,
    isLoading: false
  });

  const videoCreatorStore = useVideoCreatorStore();

  // Handle escape key, auto-focus, and event propagation
  useEffect(() => {
    if (!isOpen) return;

    // Handle escape key to close modal
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Prevent selection changes when interacting with the modal
    const handleModalEvents = (e: Event) => {
      e.stopPropagation(); // Prevent event from bubbling up
    };

    // Get the modal and overlay elements
    const modalElement = modalRef.current;
    const overlayElement = modalElement?.parentElement; // The ModalOverlay is parent of ModalContent

    // Add event listeners to the modal content
    if (modalElement) {
      modalElement.addEventListener('mousedown', handleModalEvents, true);
      modalElement.addEventListener('mouseup', handleModalEvents, true);
      modalElement.addEventListener('mousemove', handleModalEvents, true);
    }

    // Add event listeners to the modal overlay
    if (overlayElement) {
      overlayElement.addEventListener('mousedown', handleModalEvents, true);
      overlayElement.addEventListener('mouseup', handleModalEvents, true);
      overlayElement.addEventListener('mousemove', handleModalEvents, true);
    }

    // Add escape key handler
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      // Clean up all event listeners
      document.removeEventListener('keydown', handleEscapeKey);

      // Clean up modal content listeners
      if (modalElement) {
        modalElement.removeEventListener('mousedown', handleModalEvents, true);
        modalElement.removeEventListener('mouseup', handleModalEvents, true);
        modalElement.removeEventListener('mousemove', handleModalEvents, true);
      }

      // Clean up overlay listeners
      if (overlayElement) {
        overlayElement.removeEventListener('mousedown', handleModalEvents, true);
        overlayElement.removeEventListener('mouseup', handleModalEvents, true);
        overlayElement.removeEventListener('mousemove', handleModalEvents, true);
      }
    };
  }, [isOpen, onClose]);
  
  // Load speakers when menu opens
  useEffect(() => {
    if (isOpen) {
      const speakerService = editorStore.editor?.speakerService;
      if (speakerService) {
        const speakers = speakerService.getAllSpeakers(editorStore.container, editorStore);
        setSpeakerData(speakers);
      }
    } else {
      // Make sure all modals are closed when parent menu closes
      if (showInsertTextModal) {
        setShowInsertTextModal(false);
      }
    }
  }, [isOpen, editorStore, showInsertTextModal]);
  
  // Handle speaker selection
  const handleSpeakerSelect = useCallback(async (speakerLabel: string | null, datoSpeakerId: string | null) => {
    if (speakerLabel === 'new') {
      // For new speaker creation, preserve the selection
      if (onCreateNewSpeaker) {
        onCreateNewSpeaker();
      }
      return;
    }
    
    // Close the menu immediately to improve perceived performance
    onClose();
    
    try {
      // Assign speaker to current selection
      console.log('assigning speaker to current selection', speakerLabel, datoSpeakerId);
      const success = await editorStore.assignSpeakerToCurrentSelection(speakerLabel, datoSpeakerId);
    } catch (error) {
      console.error('[SpeakerSelectionMenu] Error assigning speaker to selection:', error);
    }
  }, [editorStore, onClose, onCreateNewSpeaker]);
  
  // Handle replace text functionality
  const handleGlobalReplace = useCallback(() => {
    // Get the selected text from the selection service
    try {
      console.log('handleGlobalReplace', editorStore, editorStore?.editor);
      if (editorStore?.editor?.selectionService) {
        const selectionService = editorStore.editor.selectionService;
        
        if (selectionService.hasSelection && selectionService.currentSelectionType === 'elements') {
          const selectedElements = selectionService.getSelectedElements();
          
          
          // Extract text from selected elements
          if (selectedElements.length > 0) {
            // // For simplicity, we'll use the first selected element
            // const element = selectedElements[0];
            
            // // Find the element in the container to get its value
            // if (element && editorStore.container) {
            //   const segment = editorStore.container.getSegment(element.segmentId);
            //   if (segment) {
            //     const paragraph = segment.getParagraph(element.paragraphId);
            //     if (paragraph) {
            //       const textElement = paragraph.getElement(element.id);
            //       if (textElement && textElement.type === 'text' && 'value' in textElement) {
            //         // Now we have the value
            //         if (onRequestGlobalReplace) {
            //           onRequestGlobalReplace(textElement.value);
            //           onClose();
            //           return;
            //         }
            //       }
            //     }
            //   }
            // }
            
            // If we still don't have the text, try to concatenate text from all selected elements
            let combinedText = '';
            for (const selectedElement of selectedElements) {
              // Try to get it from the container
              if (editorStore.container) {
                const seg = editorStore.container.getSegment(selectedElement.segmentId);
                if (seg) {
                  const para = seg.getParagraph(selectedElement.paragraphId);
                  if (para) {
                    const elem = para.getElement(selectedElement.id);
                    if (elem && elem.type === 'text' && 'value' in elem) {
                      combinedText += (combinedText ? ' ' : '') + elem.value;
                    }
                  }
                }
              }
            }
            
            if (combinedText) {
              if (onRequestGlobalReplace) {
                onRequestGlobalReplace(combinedText);
                onClose();
                return;
              }
            }
          }
        }
        
        console.error('[SpeakerSelectionMenu] Could not extract selected text for replacement');
      }
    } catch (error) {
      console.error('[SpeakerSelectionMenu] Error extracting text for replacement:', error);
    }
  }, [editorStore, onRequestGlobalReplace, onClose]);
  
  /**
   * Handle insert text functionality
   * 
   * IMPORTANT: When updating showInsertTextModal state, make sure to reset it to false
   * when any operation completes (success/error) to prevent the modal from staying open
   * when the SpeakerSelectionMenu is closed and reopened.
   */
  
  const handleInsertText = useCallback(() => {
    // Open the insert text modal
    setShowInsertTextModal(true);
  }, []);
  
  const handleInsertTextConfirm = useCallback(async (text: string) => {
    if (!editorStore) {
      console.error('[SpeakerSelectionMenu] No editor store found');
      setShowInsertTextModal(false);
      onClose();
      return;
    }
    
    try {
      const success = await editorStore.insertTextAfterSelection(text);
      setShowInsertTextModal(false); // Close the insert text modal
      onClose(); // Close the speaker menu
    } catch (error) {
      console.error('[SpeakerSelectionMenu] Error inserting text:', error);
      setShowInsertTextModal(false); // Ensure modal closes even on error
      onClose();
    }
  }, [editorStore, onClose, setShowInsertTextModal]);
  
  /**
   * Handle insert paragraph break functionality
   */
  const handleInsertParagraphBreak = useCallback(async () => {
    if (!editorStore) return;
    
    try {
      const success = await editorStore.insertParagraphBreakAfterSelection();
      onClose();
    } catch (error) {
      console.error('[SpeakerSelectionMenu] Error inserting paragraph break:', error);
    }
  }, [editorStore, onClose]);
  
  /**
   * Handle delete selection functionality
   */
  const handleDeleteSelection = useCallback(async () => {
    if (!editorStore) return;
    
    try {
      const success = await editorStore.deleteSelectedElements();
      onClose();
    } catch (error) {
      console.error('[SpeakerSelectionMenu] Error deleting selection:', error);
    }
  }, [editorStore, onClose]);
  
  /**
   * Handle combine paragraphs functionality
   */
  const handleCombineParagraphs = useCallback(async () => {
    if (!editorStore) return;
    
    try {
      const success = await editorStore.combineSelectedParagraphs();
      onClose();
    } catch (error) {
      console.error('[SpeakerSelectionMenu] Error combining paragraphs:', error);
    }
  }, [editorStore, onClose]);
  
  /**
   * Handle combine segments functionality
   */
  const handleCombineSegments = useCallback(async () => {
    if (!editorStore) return;
    
    try {
      const success = await editorStore.combineSelectedSegments();
      onClose();
    } catch (error) {
      console.error('[SpeakerSelectionMenu] Error combining segments:', error);
    }
  }, [editorStore, onClose]);

  /**
   * Handle save selection as clip functionality
   */
  const handleSaveAsClip = useCallback(async () => {
    if (!editorStore) return;
    
    try {
      // Show loading state
      setClipSaveState({
        isOpen: true,
        isLoading: true
      });
      
      // Close the speaker menu but keep the modal open
      onClose();
      
      const result = await editorStore.saveSelectionAsClip(undefined, videoCreatorStore);
      
      if (result.success) {
        // Show success
        setClipSaveState({
          isOpen: true,
          isLoading: false,
          videoId: result.videoId
        });
      } else {
        // Show error
        setClipSaveState({
          isOpen: true,
          isLoading: false,
          error: result.error || 'Unknown error'
        });
      }
    } catch (error) {
      console.error('[SpeakerSelectionMenu] Error saving selection as clip:', error);
      setClipSaveState({
        isOpen: true,
        isLoading: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      });
    }
  }, [editorStore, onClose]);

  /**
   * Handle color assignment functionality
   */
  const handleColorSelect = useCallback(async (color: string) => {
    if (!editorStore) return;
    
    try {
      const success = await editorStore.assignColorToSelectedElements(color);
      setShowColorSubmenu(false);
      onClose();
    } catch (error) {
      console.error('[SpeakerSelectionMenu] Error assigning color:', error);
    }
  }, [editorStore, onClose]);

  /**
   * Handle clear color functionality
   */
  const handleClearColor = useCallback(async () => {
    if (!editorStore) return;
    
    try {
      const success = await editorStore.clearColorFromSelectedElements();
      setShowColorSubmenu(false);
      onClose();
    } catch (error) {
      console.error('[SpeakerSelectionMenu] Error clearing color:', error);
    }
  }, [editorStore, onClose]);
  
  // If menu is closed, we need to also reset modal states before returning nothing
  useEffect(() => {
    if (!isOpen) {
      if (showInsertTextModal) {
        setShowInsertTextModal(false);
      }
      if (showColorSubmenu) {
        setShowColorSubmenu(false);
      }
    }
  }, [isOpen, showInsertTextModal, showColorSubmenu]);

  // Check if we have a text selection
  const selectionService = editorStore?.editor?.selectionService;
  const hasTextSelection = selectionService?.hasSelection && 
                          selectionService?.currentSelectionType === 'elements';
  // Detect multiple paragraphs or segments in the selection
  const [hasMultipleParagraphs, hasMultipleSegments] = (() => {
    if (!hasTextSelection || !selectionService) return [false, false];
    
    const selectedElements = selectionService.getSelectedElements();
    if (selectedElements.length === 0) return [false, false];
    
    // Check for multiple paragraphs
    const paragraphIds = new Set<string>();
    const segmentIds = new Set<string>();
    
    for (const element of selectedElements) {
      paragraphIds.add(element.paragraphId);
      segmentIds.add(element.segmentId);
    }
    
    return [paragraphIds.size > 1, segmentIds.size > 1];
  })();
  
  useEffect(() => {
    // Calculate position to ensure menu is visible
    const menuWidth = (modalRef.current?.offsetWidth || 250) + 10;
    const menuHeight = (modalRef.current?.offsetHeight || 300) + 10;
    
    // Adjust if menu would go off screen
    const rightEdgePosition = position.x + menuWidth;
    const bottomEdgePosition = position.y + menuHeight;
    
    const finalPosition = {
      top: bottomEdgePosition > window.innerHeight ? 
        Math.max(0, window.innerHeight - menuHeight) : 
        position.y,
      left: rightEdgePosition > window.innerWidth ? 
        Math.max(0, window.innerWidth - menuWidth) : 
        position.x
    };
    setFinalPosition(finalPosition);

  }, [position, speakerData, hasTextSelection, hasMultipleParagraphs, hasMultipleSegments]);
  

  if (!isOpen) {
    return null;
  }

  return (
    <ModalOverlay 
      onClick={(e) => {
        // Close the modal when clicking the overlay background
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      data-selection-menu="true"
      style={{ userSelect: 'none' }}
      contentEditable={false}
    >
      <ModalContainer 
        key="speaker-menu-container"
        ref={modalRef}
        style={{
          top: finalPosition.top,
          left: finalPosition.left,
          userSelect: 'none'
        }}
        data-selection-menu="true"
        contentEditable={false}
      >
        <MenuTitle key="menu-title">
          {menuTitle || "Assign Speaker to Selection"}
        </MenuTitle>
        
        <MenuContent key="menu-content">
          {/* Mapped speakers */}
          {speakerData.mapped.length > 0 && (
            <SpeakerCategory key="mapped-speakers">
              <CategoryTitle key="mapped-speakers-title">Current Speakers</CategoryTitle>
              <SpeakerList key="mapped-speakers-list">
                {speakerData.mapped.map((speaker) => (
                  <SpeakerButton
                    key={speaker.speakerLabel}
                    color={speaker.color}
                    onClick={() => handleSpeakerSelect(speaker.speakerLabel, speaker.datoSpeakerId)}
                  >
                    {speaker.name}
                  </SpeakerButton>
                ))}
              </SpeakerList>
            </SpeakerCategory>
          )}
          
          {/* Unmapped speakers */}
          {speakerData.unmapped.length > 0 && (
            <SpeakerCategory key="unmapped-speakers">
              <CategoryTitle key="unmapped-speakers-title">Anonymous Speakers</CategoryTitle>
              <SpeakerList key="unmapped-speakers-list">
                {speakerData.unmapped.map((speaker) => (
                  <SpeakerButton
                    key={speaker.speakerLabel}
                    color={speaker.color}
                    onClick={() => handleSpeakerSelect(speaker.speakerLabel, null)}
                  >
                    {speaker.name}
                  </SpeakerButton>
                ))}
              </SpeakerList>
            </SpeakerCategory>
          )}
          
          {/* Story speakers */}
          {speakerData.story.length > 0 && (
            <SpeakerCategory key="story-speakers">
              <CategoryTitle key="story-speakers-title">Story Speakers</CategoryTitle>
              <SpeakerList key="story-speakers-list">
                {speakerData.story.map((speaker) => (
                  <SpeakerButton
                    key={speaker.datoSpeakerId}
                    color={speaker.color}
                    onClick={() => handleSpeakerSelect(null, speaker.datoSpeakerId)}
                  >
                    {speaker.name}
                  </SpeakerButton>
                ))}
              </SpeakerList>
            </SpeakerCategory>
          )}
          
          {/* Album speakers */}
          {speakerData.album.length > 0 && (
            <SpeakerCategory key="album-speakers">
              <CategoryTitle key="album-speakers-title">Organization Speakers</CategoryTitle>
              <SpeakerList key="album-speakers-list">
                {speakerData.album.map((speaker) => (
                  <SpeakerButton
                    key={speaker.datoSpeakerId}
                    color={speaker.color}
                    onClick={() => handleSpeakerSelect(null, speaker.datoSpeakerId)}
                  >
                    {speaker.name}
                  </SpeakerButton>
                ))}
              </SpeakerList>
            </SpeakerCategory>
          )}
          
          {/* New speaker option */}
          {onCreateNewSpeaker && (
            <SpeakerCategory key="new-speaker-option">
              <NewSpeakerButton key="new-speaker-button" onClick={() => handleSpeakerSelect('new', '')}>
                + New Speaker
              </NewSpeakerButton>
            </SpeakerCategory>
          )}
          
          {/* Text Tools - Only show when elements are selected */}
          {hasTextSelection && (
            <SpeakerCategory key="text-tools">
              <CategoryTitle>Text Tools</CategoryTitle>
              <AlignmentButtons>
                {onRequestGlobalReplace && (
                  <AlignmentButton key="replace-text" onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleGlobalReplace();
                  }}>
                    Replace Text
                  </AlignmentButton>
                )}
                <AlignmentButton key="insert-text" onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleInsertText();
                }}>
                  Insert Text
                </AlignmentButton>
                <AlignmentButton key="insert-paragraph-break" onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleInsertParagraphBreak();
                }}>
                  Insert Paragraph Break
                </AlignmentButton>
                <AlignmentButton key="delete-selection" onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeleteSelection();
                }}>
                  Delete Selection
                </AlignmentButton>
                {hasMultipleParagraphs && (
                  <AlignmentButton key="combine-paragraphs" onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCombineParagraphs();
                  }}>
                    Combine Paragraphs
                  </AlignmentButton>
                )}
                {hasMultipleSegments && (
                  <AlignmentButton key="combine-segments" onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCombineSegments();
                  }}>
                    Combine Segments
                  </AlignmentButton>
                )}
                <AlignmentButton key="save-as-clip" onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSaveAsClip();
                }} >
                  Save Selection as Clip
                </AlignmentButton>
              </AlignmentButtons>
            </SpeakerCategory>
          )}

          {/* Color Selection - Only show when elements are selected */}
          {hasTextSelection && (
            <SpeakerCategory key="color-selection">
              <CategoryTitle>Color Selection</CategoryTitle>
              <AlignmentButtons>
                <AlignmentButton 
                  key="assign-color"
                  ref={(el) => setColorSubmenuRef(el)}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowColorSubmenu(!showColorSubmenu);
                  }}
                >
                  Assign Color ▶
                </AlignmentButton>
                <AlignmentButton 
                  key="clear-color"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleClearColor();
                  }}
                >
                  Clear Color
                </AlignmentButton>
              </AlignmentButtons>
            </SpeakerCategory>
          )}
          
          {/* Alignment tools */}
          <SpeakerCategory key="alignment-tools">
            <CategoryTitle key="alignment-tools-title">Alignment Tools</CategoryTitle>
            <AlignmentButtons key="alignment-buttons">
              {onFineAlign && (
                <AlignmentButton 
                  key="align-text-timing"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onFineAlign();
                    onClose();
                  }}
                >
                  Align Text Timing
                </AlignmentButton>
              )}
            </AlignmentButtons>
          </SpeakerCategory>
          
          <ActionButtons key="action-buttons">
            <CancelButton onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}>
              Cancel
            </CancelButton>
          </ActionButtons>
        </MenuContent>
      </ModalContainer>
      
      {/* Color submenu - rendered outside menu container */}
      {showColorSubmenu && colorSubmenuRef && (
        <ColorSubmenu
          style={{
            position: 'fixed',
            left: `${colorSubmenuRef.getBoundingClientRect().right + 5}px`,
            top: `${colorSubmenuRef.getBoundingClientRect().top}px`,
          }}
        >
          {SELECTION_COLORS.map((color) => (
            <ColorOption
              key={color.value}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleColorSelect(color.value);
              }}
            >
              <ColorIndicator color={color.value} />
              <ColorName>{color.name}</ColorName>
            </ColorOption>
          ))}
        </ColorSubmenu>
      )}
      
      {/* Insert Text Modal */}
      <InsertTextModal
        key="insert-text-modal"
        isOpen={showInsertTextModal}
        onClose={() => setShowInsertTextModal(false)}
        onConfirm={handleInsertTextConfirm}
        title="Insert Text"
      />
      
      {/* Clip Save Confirmation Modal */}
      <ClipSaveConfirmationModal
        isOpen={clipSaveState.isOpen}
        onClose={() => setClipSaveState({ isOpen: false, isLoading: false })}
        videoId={clipSaveState.videoId}
        error={clipSaveState.error}
        isLoading={clipSaveState.isLoading}
      />
    </ModalOverlay>
  );
});

SpeakerSelectionMenu.displayName = 'SpeakerSelectionMenu';

export default SpeakerSelectionMenu;

// Styled components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

/**
 * Menu container for context menus
 */
export const ModalContainer = styled.div`
  position: fixed;
  z-index: 1000;
  background-color: ${defaultTheme.colors.background};
  border: 2px solid ${defaultTheme.colors.active};
  border-radius: ${defaultTheme.borderRadius.md};
  box-shadow: 0 2px 20px rgba(76, 154, 255, 0.5);
  min-width: 250px;
  max-width: 320px;
  overflow: hidden;
  animation: fadeIn 0.2s ease-out;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

/**
 * Title for context menus
 */
export const MenuTitle = styled.div`
  font-weight: bold;
  font-size: ${defaultTheme.fontSizes.regular};
  color: ${defaultTheme.colors.text};
  padding: ${defaultTheme.spacing.sm} ${defaultTheme.spacing.md};
  background-color: ${defaultTheme.colors.backgroundSecondary};
  border-bottom: 1px solid ${defaultTheme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

/**
 * Content container for context menus
 */
export const MenuContent = styled.div`
  padding: ${defaultTheme.spacing.sm};
`;

/**
 * Category section in context menus
 */
export const SpeakerCategory = styled.div`
  margin-bottom: ${defaultTheme.spacing.sm};
`;

/**
 * Title for menu categories
 */
export const CategoryTitle = styled.div`
  font-size: ${defaultTheme.fontSizes.small};
  color: ${defaultTheme.colors.textSecondary};
  margin-bottom: ${defaultTheme.spacing.xs};
`;

/**
 * Container for speaker buttons in menus
 */
export const SpeakerList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${defaultTheme.spacing.xs};
`;

/**
 * Button for speaker selection in menus
 */
export const SpeakerButton = styled.button<{ color?: string }>`
  background-color: rgba(20, 20, 30, 0.6);
  color: ${props => props.color || defaultTheme.colors.text};
  border: 1px solid ${props => props.color || defaultTheme.colors.border};
  border-radius: ${defaultTheme.borderRadius.lg};
  padding: ${defaultTheme.spacing.xs} ${defaultTheme.spacing.sm};
  font-size: ${defaultTheme.fontSizes.small};
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
  
  &:hover {
    background-color: rgba(40, 40, 60, 0.8);
  }
`;

/**
 * Button for creating new speakers
 */
export const NewSpeakerButton = styled.button`
  background-color: rgba(20, 20, 30, 0.6);
  color: ${defaultTheme.colors.textSecondary};
  border: 1px solid ${defaultTheme.colors.border};
  border-radius: ${defaultTheme.borderRadius.lg};
  padding: ${defaultTheme.spacing.xs} ${defaultTheme.spacing.sm};
  font-size: ${defaultTheme.fontSizes.small};
  cursor: pointer;
  width: 100%;
  
  &:hover {
    background-color: rgba(40, 40, 60, 0.8);
    color: ${defaultTheme.colors.text};
  }
`;

/**
 * Container for menu action buttons
 */
export const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${defaultTheme.spacing.sm};
`;

/**
 * Cancel button for menus
 */
export const CancelButton = styled.button`
  background-color: transparent;
  color: ${defaultTheme.colors.textSecondary};
  border: 1px solid ${defaultTheme.colors.border};
  border-radius: ${defaultTheme.borderRadius.sm};
  padding: ${defaultTheme.spacing.xs} ${defaultTheme.spacing.sm};
  font-size: ${defaultTheme.fontSizes.small};
  cursor: pointer;
  
  &:hover {
    background-color: rgba(40, 40, 60, 0.8);
    color: ${defaultTheme.colors.text};
  }
`;

/**
 * Container for alignment buttons in menus
 */
export const AlignmentButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${defaultTheme.spacing.xs};
  margin-top: ${defaultTheme.spacing.xs};
`;

/**
 * Button for alignment actions
 */
export const AlignmentButton = styled.button`
  background-color: ${defaultTheme.colors.backgroundSecondary};
  color: ${defaultTheme.colors.text};
  border: 1px solid ${defaultTheme.colors.border};
  border-radius: ${defaultTheme.borderRadius.sm};
  padding: ${defaultTheme.spacing.xs} ${defaultTheme.spacing.sm};
  font-size: ${defaultTheme.fontSizes.small};
  cursor: pointer;
  text-align: left;
  
  &:hover {
    background-color: #3a3a4c;
    border-color: #6c9ba5;
  }
`;

/**
 * Color submenu container
 */
const ColorSubmenu = styled.div`
  position: fixed;
  background-color: ${defaultTheme.colors.background};
  border: 2px solid ${defaultTheme.colors.active};
  border-radius: ${defaultTheme.borderRadius.md};
  box-shadow: 0 2px 20px rgba(76, 154, 255, 0.5);
  padding: ${defaultTheme.spacing.xs};
  min-width: 150px;
  z-index: 1002;
  max-height: 300px;
  overflow-y: auto;
  animation: slideIn 0.2s ease-out;
  
  @keyframes slideIn {
    from { opacity: 0; transform: translateX(-10px); }
    to { opacity: 1; transform: translateX(0); }
  }
`;

/**
 * Color option in submenu
 */
const ColorOption = styled.button`
  display: flex;
  align-items: center;
  gap: ${defaultTheme.spacing.xs};
  width: 100%;
  background-color: transparent;
  color: ${defaultTheme.colors.text};
  border: none;
  border-radius: ${defaultTheme.borderRadius.sm};
  padding: ${defaultTheme.spacing.xs} ${defaultTheme.spacing.sm};
  font-size: ${defaultTheme.fontSizes.small};
  cursor: pointer;
  text-align: left;
  transition: background-color 0.1s ease;
  
  &:hover {
    background-color: rgba(76, 154, 255, 0.2);
  }
`;

/**
 * Color indicator circle
 */
const ColorIndicator = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  flex-shrink: 0;
`;

/**
 * Color name text
 */
const ColorName = styled.span`
  color: ${defaultTheme.colors.text};
`;